import { create } from 'zustand';
import type {
  BarData,
  ComboStatus,
  SignalData,
  OrderData,
  PositionData,
  ComboGenome,
  AlphaEvalReport,
  StrategyState,
  PortfolioMetrics,
  RdLoopState,
  JobStatus,
  AccountBalance,
  ExternalFeed,
  RiskMetrics,
  PortfolioData,
  RegimeState,
  RegimeRecord,
  EquityRecord,
  InstrumentInfo,
  OrderbookData,
  UpstreamHealth,
  PanelDataState,
} from '../types/gateway';
import type { VarConeData } from '../fixtures/mockData';

export interface WeightChangeEntry {
  combo: string;
  weight: number;
  timestamp: number;
}

const MAX_WEIGHT_HISTORY = 100;
const MAX_TOASTS = 5;

export interface Toast {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  message: string;
  timestamp: number;
}

const MAX_BARS = 10_000;
const MAX_SIGNALS = 50;

interface TerminalState {
  bars: BarData[];
  combos: Record<string, ComboStatus>;
  signals: SignalData[];
  orders: OrderData[];
  positions: PositionData[];
  genomes: Record<string, ComboGenome>;
  alphaEvals: Record<string, AlphaEvalReport>;
  strategyStates: Record<string, StrategyState>;
  metrics: PortfolioMetrics | null;
  rdLoopState: RdLoopState | null;
  jobs: JobStatus[];
  accountBalance: AccountBalance | null;
  externalFeeds: ExternalFeed[];
  riskMetrics: RiskMetrics | null;
  portfolioData: PortfolioData | null;
  varCone: VarConeData | null;
  weightHistory: WeightChangeEntry[];
  regimeState: RegimeState | null;
  regimeHistory: RegimeRecord[];
  equityHistory: EquityRecord[];
  toasts: Toast[];
  dataSource: 'live' | 'degraded' | 'mock' | 'pending';
  bootstrapError: string | null;
  // DDD #180 S1.2: per-panel data lifecycle + live upstream health.
  panelStatus: Record<string, PanelDataState>;
  upstreamHealth: UpstreamHealth | null;
  orderbook: OrderbookData | null;
  killSwitchArmed: boolean;
  arsenalMode: boolean;
  arsenalSelectedCombo: string | null;
  highContrast: boolean;
  selectedSymbol: string;
  selectedTimeframe: string;
  /** Timeframe actually used for the current bars slice (may differ from selectedTimeframe when 1m fallback is active). */
  barsTimeframeUsed: string;
  /** True when bars were fetched via the 1m fallback because dollar returned []. */
  barsIsFallback: boolean;
  instruments: Record<string, InstrumentInfo>;
  wsStatus: 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED';

  setSelectedSymbol: (symbol: string) => void;
  setSelectedTimeframe: (tf: string) => void;
  setBarsSource: (timeframeUsed: string, isFallback: boolean) => void;
  setInstruments: (list: InstrumentInfo[]) => void;
  setBars: (bars: BarData[]) => void;
  addBar: (bar: BarData) => void;
  updateCombo: (status: ComboStatus) => void;
  addSignal: (signal: SignalData) => void;
  setOrders: (orders: OrderData[]) => void;
  removeOrder: (orderId: string) => void;
  setPositions: (positions: PositionData[]) => void;
  setGenomes: (genomes: ComboGenome[]) => void;
  setAlphaEval: (report: AlphaEvalReport) => void;
  setStrategyState: (state: StrategyState) => void;
  setMetrics: (metrics: PortfolioMetrics) => void;
  setRdLoopState: (state: RdLoopState) => void;
  setJobs: (jobs: JobStatus[]) => void;
  setAccountBalance: (balance: AccountBalance) => void;
  setExternalFeeds: (feeds: ExternalFeed[]) => void;
  setRiskMetrics: (risk: RiskMetrics) => void;
  setPortfolioData: (portfolio: PortfolioData) => void;
  setVarCone: (varCone: VarConeData) => void;
  addWeightChange: (combo: string, weight: number) => void;
  setRegimeState: (regime: RegimeState) => void;
  setRegimeHistory: (history: RegimeRecord[]) => void;
  setEquityHistory: (history: EquityRecord[]) => void;
  addToast: (type: Toast['type'], message: string) => void;
  removeToast: (id: string) => void;
  setDataSource: (source: 'live' | 'degraded' | 'mock' | 'pending') => void;
  setBootstrapError: (err: string | null) => void;
  setPanelStatus: (key: string, state: PanelDataState) => void;
  setUpstreamHealth: (health: UpstreamHealth | null) => void;
  setOrderbook: (orderbook: OrderbookData) => void;
  setKillSwitch: (armed: boolean) => void;
  updateComboWeight: (comboId: string, weight: number) => void;
  setArsenalMode: (active: boolean) => void;
  setArsenalSelectedCombo: (combo: string | null) => void;
  setHighContrast: (active: boolean) => void;
  setWsStatus: (status: 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED') => void;
}

export const useTerminalStore = create<TerminalState>((set) => ({
  bars: [],
  combos: {},
  signals: [],
  orders: [],
  positions: [],
  genomes: {},
  alphaEvals: {},
  strategyStates: {},
  metrics: null,
  rdLoopState: null,
  jobs: [],
  accountBalance: null,
  externalFeeds: [],
  riskMetrics: null,
  portfolioData: null,
  varCone: null,
  weightHistory: [],
  regimeState: null,
  regimeHistory: [],
  equityHistory: [],
  toasts: [],
  dataSource: 'pending',
  bootstrapError: null,
  panelStatus: {},
  upstreamHealth: null,
  orderbook: null,
  killSwitchArmed: true,
  arsenalMode: false,
  arsenalSelectedCombo: null,
  highContrast: false,
  selectedSymbol: 'BTCUSDT',
  selectedTimeframe: 'dollar',
  barsTimeframeUsed: 'dollar',
  barsIsFallback: false,
  instruments: {},
  wsStatus: 'DISCONNECTED',

  setSelectedSymbol: (symbol) => set({ selectedSymbol: symbol }),
  setSelectedTimeframe: (tf) => set({ selectedTimeframe: tf }),
  setBarsSource: (timeframeUsed, isFallback) => set({ barsTimeframeUsed: timeframeUsed, barsIsFallback: isFallback }),
  setInstruments: (list) => set({ instruments: Object.fromEntries(list.map((i) => [i.symbol, i])) }),
  setBars: (bars) => set({ bars: bars.slice(-MAX_BARS) }),

  addBar: (bar) => set((state) => {
    const idx = state.bars.findIndex((b) => b.time === bar.time);
    let newBars: BarData[];
    if (idx >= 0) {
      newBars = [...state.bars];
      newBars[idx] = bar;
    } else {
      newBars = [...state.bars, bar].sort((a, b) => a.time - b.time);
    }
    if (newBars.length > MAX_BARS) newBars.shift();
    return { bars: newBars };
  }),

  updateCombo: (status) => set((state) => ({
    combos: { ...state.combos, [status.combo]: status },
  })),

  addSignal: (signal) => set((state) => ({
    signals: [signal, ...state.signals.slice(0, MAX_SIGNALS - 1)],
  })),

  setOrders: (orders) => set({ orders }),
  removeOrder: (orderId) => set((state) => ({
    orders: state.orders.filter((o) => o.id !== orderId),
  })),
  setPositions: (positions) => set({ positions }),
  setOrderbook: (orderbook) => set({ orderbook }),
  setKillSwitch: (armed) => set({ killSwitchArmed: armed }),

  updateComboWeight: (comboId, weight) => set((state) => {
    const combo = state.combos[comboId];
    if (!combo) return {};
    return { combos: { ...state.combos, [comboId]: { ...combo, weight } } };
  }),

  setGenomes: (genomes) => set(() => {
    const map: Record<string, ComboGenome> = {};
    for (const g of genomes) {
      map[g.name] = g;
    }
    return { genomes: map };
  }),

  setAlphaEval: (report) => set((state) => ({
    alphaEvals: { ...state.alphaEvals, [report.combo]: report },
  })),

  setStrategyState: (stratState) => set((state) => ({
    strategyStates: { ...state.strategyStates, [stratState.strategy_id]: stratState },
  })),

  setMetrics: (metrics) => set({ metrics }),
  setRdLoopState: (rdLoopState) => set({ rdLoopState }),
  setJobs: (jobs) => set({ jobs }),
  setAccountBalance: (accountBalance) => set({ accountBalance }),
  setExternalFeeds: (externalFeeds) => set({ externalFeeds }),
  setRiskMetrics: (riskMetrics) => set({ riskMetrics }),
  setPortfolioData: (portfolioData) => set({ portfolioData }),
  setVarCone: (varCone) => set({ varCone }),
  setRegimeState: (regimeState) => set({ regimeState }),
  setRegimeHistory: (regimeHistory) => set({ regimeHistory }),
  setEquityHistory: (equityHistory) => set({ equityHistory }),
  addToast: (type: Toast['type'], message: string) => set((state) => ({
    toasts: [
      { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, type, message, timestamp: Date.now() },
      ...state.toasts.slice(0, MAX_TOASTS - 1),
    ],
  })),
  removeToast: (id: string) => set((state) => ({
    toasts: state.toasts.filter((t) => t.id !== id),
  })),
  addWeightChange: (combo, weight) => set((state) => ({
    weightHistory: [
      { combo, weight, timestamp: Date.now() },
      ...state.weightHistory.slice(0, MAX_WEIGHT_HISTORY - 1),
    ],
  })),
  setDataSource: (source) => set({ dataSource: source }),
  setBootstrapError: (err) => set({ bootstrapError: err }),
  setPanelStatus: (key, state) =>
    set((s) => ({ panelStatus: { ...s.panelStatus, [key]: state } })),
  setUpstreamHealth: (health) => set({ upstreamHealth: health }),
  setArsenalMode: (active) => set({ arsenalMode: active }),
  setArsenalSelectedCombo: (combo) => set({ arsenalSelectedCombo: combo }),
  setHighContrast: (active) => set({ highContrast: active }),
  setWsStatus: (status) => set({ wsStatus: status }),
}));
